import { useEffect, useRef } from "react"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { usePublicTenant } from "@/context/PublicTenantProvider"
import {
  selectAuthStatus,
  selectRawAddresses,
  selectSelectedCheckoutAddressId,
  selectUserId,
} from "@/selectors/auth.selectors"
import {
  initializeOnlySavedAddressForSession,
  restoreCheckoutAddressForSession,
} from "@/slices/checkout-address-slice"
import { logCheckoutAddressDiag } from "@/utils/checkout-address-diagnostics"

/**
 * Centralized checkout-address initialization (hosted by AuthBootstrapGate).
 *
 * Step 1 — new-document restoration: once per (customer, restaurant) as soon as the session
 * is authenticated and the tenant is resolved; only when nothing is selected yet and the
 * stored entry matches both scopes.
 *
 * Step 2 — single-address initialization: when the raw list is known to hold exactly one
 * address and there is still no selection, that address becomes the selection (and is
 * mirrored like any selection). Guarded inside the thunk on live store state, so an
 * explicit or restored selection is never overwritten, a list refetch cannot change an
 * existing selection, and zero / multiple addresses keep the existing rules.
 */
export function useRestoreCheckoutAddress(): void {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectAuthStatus)
  const userId = useAppSelector(selectUserId)
  const rawAddresses = useAppSelector(selectRawAddresses)
  const selectedAddressId = useAppSelector(selectSelectedCheckoutAddressId)
  const { restaurantId } = usePublicTenant()
  const restoredScopeRef = useRef<string | null>(null)

  useEffect(() => {
    if (status !== "authenticated" || !userId || !restaurantId) return
    const scope = `${userId}|${restaurantId}`
    if (restoredScopeRef.current === scope) return
    restoredScopeRef.current = scope
    const restored = dispatch(restoreCheckoutAddressForSession({ userId, restaurantId }))
    if (restored) {
      logCheckoutAddressDiag("checkout_address_restored", { restaurantId, addressId: restored })
    }
  }, [dispatch, status, userId, restaurantId])

  useEffect(() => {
    if (status !== "authenticated" || !userId || !restaurantId) return
    if (selectedAddressId || rawAddresses.length !== 1) return
    const initialized = dispatch(
      initializeOnlySavedAddressForSession({
        userId,
        restaurantId,
        addressIds: rawAddresses.map((address) =>
          address.address_id != null ? String(address.address_id) : null
        ),
      })
    )
    if (initialized) {
      logCheckoutAddressDiag("checkout_address_selected", {
        restaurantId,
        addressId: initialized,
        source: "auto",
        addressCount: 1,
      })
    }
  }, [dispatch, status, userId, restaurantId, selectedAddressId, rawAddresses])
}
